import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateAfterSupportCreate } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { firstFieldError, humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { userApi } from 'features/home/home.api';
import { Button } from 'shared/components/Button';
import { SelectInput, TextArea } from 'shared/components/Input';
import { Card, ErrorState, Skeleton } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { asList, localizedText } from 'shared/utilities/format';

const MAX = 500;

export function NewTicketPage() {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const categoriesQuery = useQuery({
    queryKey: queryKeys.supportCategories,
    queryFn: () => userApi.supportCategories(),
  });
  const [categoryId, setCategoryId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError('');
    setFieldErrors({});
    try {
      const data = await userApi.createTicket({
        category_id: Number(categoryId),
        message,
      });
      await invalidateAfterSupportCreate(queryClient);
      setCreated(data);
    } catch (err) {
      setFieldErrors(err.errors || {});
      setError(humanizeApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  if (categoriesQuery.isLoading) {
    return <Skeleton rows={5} />;
  }
  if (categoriesQuery.isError) {
    return <ErrorState title={humanizeApiError(categoriesQuery.error, t)} onRetry={() => categoriesQuery.refetch()} retryLabel={t('actions.retry')} />;
  }

  const categories = asList(categoriesQuery.data);

  if (created) {
    return (
      <Card className="success-card">
        <PageMeta titleKey="meta.support" />
        <h1>{t('support.success')}</h1>
        <p data-testid="ticket-reference">{t('support.reference', { reference: created.reference })}</p>
        <Link className="btn btn-primary" to={paths.supportTicket(created.id)}>{t('actions.view')}</Link>
      </Card>
    );
  }

  return (
    <div className="contact-layout">
      <form className="form-page form-page-wide" onSubmit={handleSubmit}>
        <PageMeta titleKey="meta.support" />
        <h1>{t('support.newTitle')}</h1>
      <SelectInput
        id="category"
        label={t('support.category')}
        value={categoryId}
        error={firstFieldError(fieldErrors, 'category_id')}
        onChange={(event) => setCategoryId(event.target.value)}
        required
      >
        <option value="">{t('support.category')}</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {localizedText(category, locale, ['title', 'name'])}
          </option>
        ))}
      </SelectInput>
      <TextArea
        id="message"
        label={t('support.message')}
        maxLength={MAX}
        value={message}
        error={firstFieldError(fieldErrors, 'message')}
        hint={t('support.counter', { count: message.length, max: MAX })}
        onChange={(event) => setMessage(event.target.value)}
        required
      />
      {error ? <p className="field-error" role="alert">{error}</p> : null}
      <Button type="submit" loading={submitting} disabled={message.length === 0 || message.length > MAX}>
        {t('actions.sendTicket')}
      </Button>
      </form>
      <Card>
        <h2>{t('support.title')}</h2>
        <p className="muted">{t('support.counter', { count: message.length, max: MAX })}</p>
      </Card>
    </div>
  );
}
